import { useEffect, useState } from 'react'
import { actionLabel } from './acervoHelpers'
import { candidateFeedback, candidateIssues, candidateRankings, relevanceReason, RELEVANCE_DECISIONS } from './researchRelevance'

function IssueRelevance({ candidate, issue, ranking, feedback, document, research, disabled }) {
  const [decision, setDecision] = useState(feedback?.decision || '')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')
  useEffect(() => { setDecision(feedback?.decision || ''); setNotes(''); setError('') }, [feedback?.decision, feedback?.recordedAt])
  const save = async (event) => {
    event.preventDefault()
    if (disabled || !document || !decision || notes.length > 1000) return
    try {
      setError('')
      const saved = await research.feedback(candidate.candidateId, issue.issueId, decision, notes)
      if (saved) setNotes('')
    } catch (failure) { setError(failure.message) }
  }
  const relevant = ranking?.matchedIssues?.includes(issue.issueId)
  const reasons = [...(ranking?.missingEvidence || []), ...(ranking?.selectionReasons || []),
    ...(ranking?.eligibilityReasons || []).filter((reason) => reason !== 'RANKING_NEVER_RELEASES_CITATION')]
  const features = (ranking?.featureBreakdown || []).filter((value) => value.reason)
  const recordedDate = feedback?.recordedAt ? new Date(feedback.recordedAt) : null
  return <section className="legal-research-issue-relevance">
    <h5>{issue.question}</h5>
    {issue.actionCodes?.length > 0 && <p className="acervo-muted">Ação: {issue.actionCodes.map((code) => actionLabel(code)).join(' · ')}</p>}
    {ranking ? <>
      <p><strong>{relevant ? 'Aderência identificada para conferência' : 'Aderência ainda não estabelecida'}</strong>
        {Number.isInteger(ranking.rank) && ranking.rank > 0 ? ` · Prioridade de leitura ${ranking.rank} nesta questão` : ''}</p>
      {['OpposesThesis', 'Mixed'].includes(ranking.thesisRelation) && <p className="acervo-notice">Há entendimento contrário ou misto para examinar. Isso não torna o julgado irrelevante.</p>}
      <details><summary>Motivos e limites da pertinência</summary>
        {features.length > 0 && <ul>{features.map((value, index) => <li key={`${value.name}-${index}`}>{value.reason}</li>)}</ul>}
        {reasons.length > 0 && <ul>{[...new Set(reasons)].map((reason) => <li key={reason}>{relevanceReason(reason)}</li>)}</ul>}
      </details>
    </> : <p className="acervo-muted">Este vínculo ainda não possui avaliação de pertinência no ranking.</p>}
    {feedback && <p className="acervo-notice" role="status">Sua avaliação registrada: <strong>{RELEVANCE_DECISIONS[feedback.decision]}</strong>
      {recordedDate && Number.isFinite(recordedDate.valueOf()) ? ` · ${recordedDate.toLocaleString('pt-BR')}` : ''}.
      {feedback.decision === 'NotPertinent' ? ' Esta questão não autoriza o uso da referência na minuta.' : ' A autorização para a minuta exige a conferência própria abaixo.'}
    </p>}
    {document ? <form className="legal-research-feedback" onSubmit={save}>
      <fieldset disabled={disabled}>
        <legend>Avaliar pertinência para melhorar próximas pesquisas</legend>
        <label className="acervo-field">Relação do julgado com esta questão
          <select value={decision} onChange={(event) => setDecision(event.target.value)} required>
            <option value="">Selecione uma avaliação</option>
            {Object.entries(RELEVANCE_DECISIONS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label className="acervo-field">Justificativa opcional
          <textarea value={notes} rows={2} onChange={(event) => setNotes(event.target.value)} aria-invalid={notes.length > 1000} />
          <small>{notes.length} / 1.000 caracteres.</small>
        </label>
        <p className="acervo-muted">A avaliação orienta a prioridade em contextos semelhantes. Não confirma vigência nem autoriza citação. Um entendimento contrário também pode ser pertinente.</p>
        <button type="submit" className="acervo-button" disabled={!decision || notes.length > 1000}>Registrar avaliação de pertinência</button>
      </fieldset>
      {error && <p className="acervo-error" role="alert">{error}</p>}
    </form> : <p className="acervo-muted">Obtenha o inteiro teor para registrar a pertinência desta versão do julgado.</p>}
  </section>
}

export default function ResearchCandidateRelevance({ candidate, document, result, research, disabled }) {
  const rankings = candidateRankings(result, candidate)
  const issues = candidateIssues(result, candidate)
  return <details className="legal-research-relevance" open>
    <summary>Pertinência à ação e às questões do caso</summary>
    {issues.map((issue) => <IssueRelevance key={`${candidate.candidateId}-${candidate.documentVersionId}-${issue.issueId}`}
      candidate={candidate} issue={issue} document={document} ranking={rankings.find((value) => value.issueId === issue.issueId)}
      feedback={candidateFeedback(result, candidate.candidateId, issue.issueId)} research={research} disabled={disabled} />)}
    {issues.length === 0 && <p className="acervo-muted">Ainda não há questão associada a este candidato.</p>}
  </details>
}
